import { useLayoutEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { isAdminPath } from '../utils/adminArea'

export default function MobileNav() {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  // isLoading is true only while the initial session check (if a token exists) is
  // still in flight -- render through that window to avoid a flash-of-no-nav for a
  // user who's about to be confirmed logged in. Once it resolves, trust `user`
  // alone; a leftover/invalid token in localStorage must never keep this visible.
  // Hidden on the admin screens (a desk tool with their own navigation) and on the required
  // profile step, where the bar would only offer ways out.
  const hiddenHere = isAdminPath(location.pathname) || location.pathname === '/complete-profile'
  const visible = (!!user || isLoading) && !hiddenHere
  const navRef = useRef<HTMLElement>(null)

  // The bar is position:fixed, so anything else pinned to the bottom of the screen (like
  // Browse's pagination) would end up underneath it. Publish its real height (0 when it's
  // hidden above the mobile breakpoint) as --mobile-nav-offset so those elements can sit above it.
  useLayoutEffect(() => {
    const nav = navRef.current
    if (!visible || !nav) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--mobile-nav-offset', `${nav.getBoundingClientRect().height}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(nav, { box: 'border-box' })
    return () => {
      observer.disconnect()
      root.style.removeProperty('--mobile-nav-offset')
    }
  }, [visible])

  if (!visible) return null

  const isActive = (path: string) => location.pathname === path

  const homePath = user ? '/dashboard' : '/'

  return (
    <nav ref={navRef} className="mobile-nav">
      <Link to={homePath} className={`mobile-nav-item ${isActive(homePath) ? 'active' : ''}`}>
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
        <span>Home</span>
      </Link>
      <Link to="/browse" className={`mobile-nav-item ${isActive('/browse') ? 'active' : ''}`}>
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span>Browse</span>
      </Link>
      <Link to="/my-rentals" className={`mobile-nav-item ${isActive('/my-rentals') ? 'active' : ''}`}>
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <span>My Rentals</span>
      </Link>

    </nav>
  )
}
